import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, Copy, Check, KeyRound, Send, ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";

const card = "rounded-xl border border-slate-700 bg-[#1E293B]";
const base = typeof window !== "undefined" ? window.location.origin : "https://your-abcdpay-domain";

function CodeBlock({ code, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Couldn't copy — select the code manually");
    }
  };
  return (
    <div className="relative group">
      <pre data-testid={testid} className="bg-[#0F172A] border border-slate-700 rounded-lg p-4 text-xs font-mono text-slate-200 overflow-x-auto leading-relaxed whitespace-pre">{code}</pre>
      <Button size="sm" variant="outline" onClick={copy} className="absolute top-2 right-2 h-7 border-slate-600 bg-[#1E293B] text-slate-300 hover:bg-slate-800 opacity-0 group-hover:opacity-100 transition-opacity">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-sm font-bold flex items-center justify-center shrink-0">{n}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-200">{title}</div>
        <div className="mt-2 space-y-2">{children}</div>
      </div>
    </div>
  );
}

const curl1 = `curl -X POST ${base}/api/v1/qr/request \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"amount": 500, "order_id": "ORD_1001", "user_id": "USR_42"}'`;

const curl2 = `curl -X POST ${base}/api/v1/payments/submit \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "qr_id": "QR_ID_FROM_STEP_1",
    "submission_id": "SUBMISSION_ID_FROM_STEP_1",
    "transaction_id": "T1234567890",
    "amount": 500,
    "customer_reference": "cust_001",
    "order_id": "ORD_1001",
    "user_id": "USR_42",
    "payer_upi": "payer@okaxis",
    "payer_name": "Ravi Kumar",
    "utr": "412345678901"
  }'`;

const node = `const BASE = "${base}";
const API_KEY = "YOUR_API_KEY";
const headers = { Authorization: "Bearer " + API_KEY, "Content-Type": "application/json" };

// 1) Ask ABCD Pay which QR to show for this amount
const qrRes = await fetch(BASE + "/api/v1/qr/request", {
  method: "POST", headers,
  body: JSON.stringify({ amount: 500 }),
});
const { qr_id, upi_id, qr_image_url } = await qrRes.json();
// -> show upi_id / qr_image_url to your customer

// 2) After the customer pays, submit the claim
const subRes = await fetch(BASE + "/api/v1/payments/submit", {
  method: "POST", headers,
  body: JSON.stringify({
    qr_id,
    transaction_id: "T1234567890",
    amount: 500,
    customer_reference: "cust_001",
    order_id: "ORD_1001",      // optional
    user_id: "USR_42",         // optional
    payer_upi: "payer@okaxis", // optional
    payer_name: "Ravi Kumar",  // optional
    utr: "412345678901",       // optional
  }),
});
console.log(await subRes.json());
// -> { submission_id, status: "pending" | "received", ... }`;

const python = `import requests
BASE = "${base}"
HEADERS = {"Authorization": "Bearer YOUR_API_KEY"}

# 1) Ask ABCD Pay which QR to show for this amount
qr = requests.post(f"{BASE}/api/v1/qr/request", headers=HEADERS,
                   json={"amount": 500}).json()
# -> show qr["upi_id"] / qr["qr_image_url"] to your customer

# 2) After the customer pays, submit the claim
resp = requests.post(f"{BASE}/api/v1/payments/submit", headers=HEADERS, json={
    "qr_id": qr["qr_id"],
    "transaction_id": "T1234567890",
    "amount": 500,
    "customer_reference": "cust_001",
    "order_id": "ORD_1001",       # optional
    "user_id": "USR_42",          # optional
    "payer_upi": "payer@okaxis",  # optional
    "payer_name": "Ravi Kumar",   # optional
    "utr": "412345678901",        # optional
}).json()
print(resp)`;

const whNode = `import crypto from "crypto";
import express from "express";
const app = express();
const WEBHOOK_SECRET = "YOUR_WEBHOOK_SECRET";

// Use the RAW request body to verify the signature
app.post("/abcdpay/webhook", express.raw({ type: "application/json" }), (req, res) => {
  const signature = req.header("X-Taoronpay-Signature");
  const expected = "sha256=" + crypto
    .createHmac("sha256", WEBHOOK_SECRET)
    .update(req.body)
    .digest("hex");

  // Reject forged calls
  if (signature !== expected) return res.status(401).send("bad signature");

  const event = req.header("X-Taoronpay-Event"); // payment.matched, etc.
  const payload = JSON.parse(req.body.toString());
  // ... update your order using payload.status / payload.match_insight ...

  res.status(200).send("ok"); // MUST respond 2xx within 5 seconds
});`;

const whPython = `import hmac, hashlib
from fastapi import FastAPI, Request, Response

app = FastAPI()
WEBHOOK_SECRET = "YOUR_WEBHOOK_SECRET"

@app.post("/abcdpay/webhook")
async def webhook(request: Request):
    raw = await request.body()  # raw bytes, do not re-serialize
    signature = request.headers.get("X-Taoronpay-Signature", "")
    expected = "sha256=" + hmac.new(WEBHOOK_SECRET.encode(), raw, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(expected, signature):
        return Response(status_code=401)

    event = request.headers.get("X-Taoronpay-Event")
    # ... update your order ...
    return {"ok": True}  # respond 2xx within 5 seconds`;

const events = [
  ["payment.matched", "Tx ID, business and amount all matched", "text-emerald-400"],
  ["payment.mismatch", "Tx ID matched but the amount differs", "text-amber-400"],
  ["payment.wrong_business", "Tx ID was recorded under a different business", "text-amber-400"],
  ["payment.not_found", "No received payment found (sent after a 10-min grace)", "text-rose-400"],
];

const errors = [
  ["400", "Missing/invalid fields, or qr_id not yours"],
  ["401", "Missing, invalid or rotated API key"],
  ["409", "Duplicate transaction_id for this merchant"],
  ["429", "Rate limit exceeded (60 requests / minute)"],
];

export function ApiGuide() {
  return (
    <div className={`${card} p-5`} data-testid="merchant-api-guide">
      <div className="flex items-center gap-2 text-slate-300">
        <BookOpen className="w-4 h-4 text-emerald-400" />
        <h2 className="font-bold tracking-tight">API &amp; Webhook Guide</h2>
      </div>
      <p className="text-sm text-slate-500 mt-1">Follow these steps to accept payments programmatically. All requests go to your ABCD Pay domain below.</p>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Base URL</span>
        <code className="bg-[#0F172A] border border-slate-700 rounded px-2 py-1 font-mono text-emerald-300">{base}</code>
      </div>

      <div className="mt-5 space-y-5">
        <Step n="1" title="Authenticate every request">
          <p className="text-sm text-slate-400 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5 text-emerald-400" /> Send your API key as a Bearer token. Get or rotate it from the <span className="text-slate-300 font-medium">API Key</span> card above — the key is shown only once.</p>
          <code className="block bg-[#0F172A] border border-slate-700 rounded px-3 py-2 text-xs font-mono text-slate-300">Authorization: Bearer YOUR_API_KEY</code>
        </Step>

        <Step n="2" title="Request a QR for the amount you want to collect">
          <p className="text-sm text-slate-400">ABCD Pay returns an active QR assigned to you whose allowed range fits the amount. Show its <span className="font-mono text-slate-300">upi_id</span> / <span className="font-mono text-slate-300">qr_image_url</span> to your customer.</p>
        </Step>

        <Step n="3" title="Submit the payment claim after the customer pays">
          <p className="text-sm text-slate-400">Send the <span className="font-mono text-slate-300">qr_id</span> from step 2 plus the customer's transaction ID and amount. It runs through the same matching engine you see in the portal.</p>
          <Tabs defaultValue="curl" className="mt-2">
            <TabsList className="bg-[#0F172A] border border-slate-700">
              <TabsTrigger value="curl" data-testid="guide-tab-curl">cURL</TabsTrigger>
              <TabsTrigger value="node" data-testid="guide-tab-node">Node.js</TabsTrigger>
              <TabsTrigger value="python" data-testid="guide-tab-python">Python</TabsTrigger>
            </TabsList>
            <TabsContent value="curl" className="mt-2 space-y-2">
              <div className="text-xs text-slate-500">Step 2 — request QR</div>
              <CodeBlock code={curl1} testid="guide-code-curl-1" />
              <div className="text-xs text-slate-500 mt-2">Step 3 — submit payment</div>
              <CodeBlock code={curl2} testid="guide-code-curl-2" />
            </TabsContent>
            <TabsContent value="node" className="mt-2"><CodeBlock code={node} testid="guide-code-node" /></TabsContent>
            <TabsContent value="python" className="mt-2"><CodeBlock code={python} testid="guide-code-python" /></TabsContent>
          </Tabs>
        </Step>

        <Step n="4" title="Receive webhooks for every status change">
          <p className="text-sm text-slate-400 flex items-center gap-1.5"><Send className="w-3.5 h-3.5 text-emerald-400" /> Set your Webhook URL above. We POST a JSON event whenever a submission's status changes. Always verify the signature and respond <span className="font-mono text-slate-300">2xx within 5s</span>.</p>
          <div className="grid sm:grid-cols-2 gap-2 mt-1">
            {events.map(([e, d, c]) => (
              <div key={e} className="rounded-lg border border-slate-700 bg-[#0F172A] p-2.5">
                <code className={`text-xs font-mono ${c}`}>{e}</code>
                <div className="text-xs text-slate-500 mt-0.5">{d}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-700 bg-[#0F172A] p-3 mt-1 text-xs text-slate-400">
            <div className="font-semibold text-slate-300 mb-1">Headers we send</div>
            <div className="font-mono space-y-0.5">
              <div>X-Taoronpay-Event: payment.matched</div>
              <div>X-Taoronpay-Signature: sha256=&lt;hmac of raw body&gt;</div>
              <div>X-Taoronpay-Delivery-Id: whd_xxxxx</div>
              <div>X-Taoronpay-Timestamp: 2026-06-01T10:00:00Z</div>
            </div>
          </div>
        </Step>

        <Step n="5" title="Verify the webhook signature">
          <p className="text-sm text-slate-400 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Compute an HMAC-SHA256 of the <span className="font-medium text-slate-300">raw request body</span> using your Webhook Secret and compare it to the header.</p>
          <Tabs defaultValue="node" className="mt-2">
            <TabsList className="bg-[#0F172A] border border-slate-700">
              <TabsTrigger value="node" data-testid="guide-wh-tab-node">Node.js</TabsTrigger>
              <TabsTrigger value="python" data-testid="guide-wh-tab-python">Python</TabsTrigger>
            </TabsList>
            <TabsContent value="node" className="mt-2"><CodeBlock code={whNode} testid="guide-code-wh-node" /></TabsContent>
            <TabsContent value="python" className="mt-2"><CodeBlock code={whPython} testid="guide-code-wh-python" /></TabsContent>
          </Tabs>
          <p className="text-xs text-slate-500 mt-2">Failed deliveries are retried automatically with backoff (1m, 5m, 30m, 2h, 6h). You can also re-send any delivery from the logs below.</p>
        </Step>

        <div>
          <div className="font-semibold text-slate-200 text-sm mb-2">Error codes</div>
          <div className="overflow-hidden rounded-lg border border-slate-700">
            {errors.map(([code, desc], i) => (
              <div key={code} className={`flex items-start gap-3 px-3 py-2 text-xs ${i % 2 ? "bg-[#0F172A]" : "bg-[#1E293B]"}`}>
                <code className="font-mono text-rose-400 w-8 shrink-0">{code}</code>
                <span className="text-slate-400">{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
